// src/components/AdminTools.tsx
import React, { useState } from 'react';
import { Button, Text, View, Flex, TextField, Heading, Divider, Alert } from '@aws-amplify/ui-react';
import { generateClient } from 'aws-amplify/api';
import { deleteTripAndAssociatedPhotos } from '../services/tripService';

// Custom GraphQL query for initializing storage tiers
const INITIALIZE_STORAGE_TIERS = /* GraphQL */ `
  mutation InitStorageTiers {
    initStorageTiers {
      id
      tier
      name
      storageBytes
      pricePerMonth
      features
      isDefault
      createdAt
      updatedAt
    }
  }
`;

// GraphQL query to verify a trip exists
const GET_TRIP = /* GraphQL */ `
  query GetTrip($id: ID!) {
    getTrip(id: $id) {
      id
      tripName
      createdBy
      storageUsed
    }
  }
`;

const client = generateClient();

const AdminTools: React.FC = () => {
  // Original storage tier state
  const [initLoading, setInitLoading] = useState(false);
  const [initResult, setInitResult] = useState<any>(null);
  const [initError, setInitError] = useState<string | null>(null);
  
  // New trip deletion state
  const [tripId, setTripId] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [tripDetails, setTripDetails] = useState<any>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Handle storage tier initialization (existing functionality)
  const handleInitializeTiers = async () => {
    try {
      setInitLoading(true);
      setInitError(null);
      
      // Call the mutation
      const response = await client.graphql({
        query: INITIALIZE_STORAGE_TIERS,
        authMode: "userPool"
      });
      
      //setInitResult(response.data.initStorageTiers);
    } catch (err: any) {
      console.error('Error initializing storage tiers:', err);
      setInitError(err.message || 'An error occurred while initializing storage tiers');
    } finally {
      setInitLoading(false);
    }
  };

  // Handle trip ID input change
  const handleTripIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTripId(e.target.value);
    // Reset states when input changes
    setTripDetails(null);
    setDeleteError(null);
    setDeleteSuccess(null);
    setConfirmDelete(false);
  };

  // Verify if trip exists before deletion
  const verifyTrip = async () => {
    if (!tripId.trim()) {
      setDeleteError('Please enter a valid Trip ID');
      return;
    }

    try {
      setVerifyLoading(true);
      setDeleteError(null);
      setTripDetails(null);
      
      const response: any = await client.graphql({
        query: GET_TRIP,
        variables: { id: tripId },
        authMode: "userPool"
      });
      
      if (response.data.getTrip) {
        setTripDetails(response.data.getTrip);
        setConfirmDelete(true);
      } else {
        setDeleteError('Trip not found. Please check the ID and try again.');
      }
    } catch (err: any) {
      console.error('Error verifying trip:', err);
      setDeleteError(err.message || 'Error verifying trip existence. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  // Handle trip deletion
  const handleDeleteTrip = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      setDeleteSuccess(null);
      await deleteTripAndAssociatedPhotos(tripId);
      setDeleteSuccess(`Trip "${tripDetails?.tripName || tripId}" successfully deleted with all photos.`);
      setTripId('');
      setTripDetails(null);
      setConfirmDelete(false);
      
    } catch (err: any) {
      console.error('Error deleting trip:', err);
      setDeleteError(err.message || 'An error occurred while deleting the trip');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    setConfirmDelete(false);
    setTripDetails(null);
  };

  return (
    <View padding="1.5rem" backgroundColor="#1A1A1A" borderRadius="medium">
      <Heading level={3} marginBottom="1rem">Admin Tools</Heading>
      
      <Divider marginBottom="1.5rem" />
      
      {/* Storage Tier Initialization Section */}
      <Heading level={4} marginBottom="1rem">Storage Tier Management</Heading>
      <Button 
        onClick={handleInitializeTiers}
        isLoading={initLoading}
        loadingText="Initializing..."
        variation="primary"
        marginBottom="1rem"
      >
        Initialize Storage Tiers
      </Button>
      
      {initError && (
        <Text color="red" marginTop="0.5rem" marginBottom="1rem">
          Error: {initError}
        </Text>
      )}
      
      {initResult && (
        <View marginTop="0.5rem" marginBottom="1.5rem">
          <Text fontWeight="bold">Initialization Complete!</Text>
          <Text>{Array.isArray(initResult) ? initResult.length : 0} storage tiers have been created/updated.</Text>
        </View>
      )}
      
      <Divider marginTop="1.5rem" marginBottom="1.5rem" />
      
      {/* Trip Deletion Section */}
      <Heading level={4} marginBottom="1rem">Trip Management</Heading>
      
      {deleteSuccess && (
        <Alert 
          variation="success" 
          isDismissible={true} 
          onDismiss={() => setDeleteSuccess(null)}
          marginBottom="1rem"
        >
          {deleteSuccess}
        </Alert>
      )}
      
      {deleteError && (
        <Alert 
          variation="error" 
          isDismissible={true} 
          onDismiss={() => setDeleteError(null)}
          marginBottom="1rem"
        >
          {deleteError}
        </Alert>
      )}
      
      {!confirmDelete ? (
        <Flex direction="column" gap="1rem">
          <TextField
            label="Trip ID"
            placeholder="Enter the trip ID to delete"
            value={tripId}
            onChange={handleTripIdChange}
            descriptiveText="This action will permanently delete the trip and all associated photos from S3."
            isRequired
          />
          
          <Button
            variation="destructive"
            onClick={verifyTrip}
            isLoading={verifyLoading}
            loadingText="Verifying..."
            isDisabled={!tripId.trim()}
          >
            Verify Trip
          </Button>
        </Flex>
      ) : (
        <View backgroundColor="#2A1F1F" padding="1.5rem" borderRadius="medium" marginBottom="1.5rem">
          <Heading level={5} marginBottom="1rem">Confirm Trip Deletion</Heading>
          
          <Flex direction="column" gap="0.75rem" marginBottom="1.5rem">
            <Flex justifyContent="space-between">
              <Text fontWeight="bold">Trip ID:</Text>
              <Text>{tripDetails.id}</Text>
            </Flex>
            
            <Flex justifyContent="space-between">
              <Text fontWeight="bold">Trip Name:</Text>
              <Text>{tripDetails.tripName}</Text>
            </Flex>
            
            <Flex justifyContent="space-between">
              <Text fontWeight="bold">Created By:</Text>
              <Text>{tripDetails.createdBy}</Text>
            </Flex>
            
            <Flex justifyContent="space-between">
              <Text fontWeight="bold">Storage Used:</Text>
              <Text>{formatFileSize(tripDetails.storageUsed || 0)}</Text>
            </Flex>
          </Flex>
          
          <Text color="#FF6B6B" marginBottom="1.5rem" fontWeight="medium">
            Warning: This action will permanently delete this trip and all its photos from S3.
            This action cannot be undone.
          </Text>
          
          <Flex justifyContent="flex-end" gap="1rem">
            <Button 
              variation="link" 
              onClick={handleCancelDelete}
            >
              Cancel
            </Button>
            
            <Button 
              variation="destructive" 
              onClick={handleDeleteTrip}
              isLoading={deleteLoading}
              loadingText="Deleting..."
            >
              Confirm Deletion
            </Button>
          </Flex>
        </View>
      )}
    </View>
  );
};

// Helper function to format file size
function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}

export default AdminTools;